import { SearchOutlined } from '@ant-design/icons';
import { SizeType } from 'antd/es/config-provider/SizeContext';

import { Input, InputRef, theme } from 'antd';
import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';

export interface SearchInputProps {
	autoFocus?: boolean;
	className?: string;
	debounceDelay?: number;
	defaultValue?: string;
	id?: string;
	onChange: (value: string) => void;
	placeholder?: string;
	size?: SizeType;
}

export type SearchInputRef = {
	inputRef: InputRef | null;
	setText: (value: string) => void;
};

export const SearchInput = forwardRef<SearchInputRef, SearchInputProps>(
	({ autoFocus, className, debounceDelay = 300, defaultValue, id, onChange, placeholder = 'Search', size }, ref) => {
		const { token } = theme.useToken();
		const [ text, setText ] = useState(defaultValue);
		const inputRef = useRef<InputRef>(null);
		const onChangeRef = useRef(onChange);
		const timeoutRef = useRef(0);

		useImperativeHandle(
			ref,
			() => ({
				inputRef: inputRef.current,
				setText: (value) => setText(value)
			}),
			[]
		);

		onChangeRef.current = onChange;

		const handleChange = useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				const value = e.target?.value;

				if (timeoutRef.current) {
					window.clearTimeout(timeoutRef.current);
				}

				timeoutRef.current = window.setTimeout(() => {
					onChangeRef.current(value?.trim() ?? '');
				}, debounceDelay);

				setText(e.target?.value);
			},
			[ debounceDelay ]
		);

		return (
			<Input
				allowClear
				autoFocus={ autoFocus }
				className={ className }
				defaultValue={ defaultValue }
				id={ id }
				onChange={ handleChange }
				placeholder={ placeholder }
				ref={ inputRef }
				size={ size }
				suffix={ !text && <SearchOutlined style={ { color: token.colorTextPlaceholder } } /> }
				title='Search'
				value={ text } />
		);
	}
);
